import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Save } from 'lucide-react';
import { Actions as ORGANISATION_ACTIONS } from '../../store/actions/organisationActions';
import { Button } from '../ui/button';
import { newSelect as Select } from '../ui/newSelect';
import PlanEntitlementsSummary from './PlanEntitlementsSummary';

const LEGACY_VALUE = '__legacy__';
const DIRECT_PLAN_OPTIONS = [
  { value: LEGACY_VALUE, label: 'Legacy / unmapped' },
  { value: 'discover', label: 'Discover' },
  { value: 'measure', label: 'Measure' },
  { value: 'execute', label: 'Execute' },
  { value: 'coordinate', label: 'Coordinate' },
  { value: 'govern', label: 'Govern' },
];
const WHITE_LABEL_PLAN_OPTIONS = [
  { value: LEGACY_VALUE, label: 'Legacy / unmapped' },
  { value: 'wl_measure_lite', label: 'Measure (white-label lite)' },
];

/**
 * Plan assignment for authorised commercial admins and entitlement display.
 */
const OrganisationPlanSettings = ({
  organisation,
  canManageSettings,
  updateOrganisationEntitlements,
  getOrganisation,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState(organisation?.planId || LEGACY_VALUE);
  const [saving, setSaving] = useState(false);
  const isWhiteLabelOrganisation = String(organisation?.whiteLabelId || '0') !== '0';
  const planOptions = isWhiteLabelOrganisation ? WHITE_LABEL_PLAN_OPTIONS : DIRECT_PLAN_OPTIONS;

  useEffect(() => {
    setSelectedPlanId(organisation?.planId || LEGACY_VALUE);
  }, [organisation?.id, organisation?.planId]);

  useEffect(() => {
    if (organisation?.id) getOrganisation();
  }, [organisation?.id, getOrganisation]);

  const savePlan = async () => {
    if (!organisation?.id) return;
    setSaving(true);
    await updateOrganisationEntitlements({
      id: organisation.id,
      planId: selectedPlanId === LEGACY_VALUE ? null : selectedPlanId,
    });
    setSaving(false);
  };

  return (
    <div className='mt-6 space-y-4'>
      {canManageSettings && (
        <div className='rounded-xl border border-border bg-card p-6 shadow-sm'>
          <h2 className='text-lg font-semibold text-foreground'>Plan assignment</h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            Set the internal plan used to resolve this organisation&apos;s entitlements. Stripe billing is managed separately.
          </p>
          <div className='mt-4 flex flex-wrap items-end gap-3'>
            <div className='w-full max-w-xs'>
              <label className='mb-1 block text-sm font-medium text-foreground'>Plan</label>
              <Select
                options={planOptions}
                value={selectedPlanId}
                onChange={setSelectedPlanId}
                placeholder='Select plan'
                className='w-full'
              />
            </div>
            <Button
              type='button'
              loading={saving}
              disabled={saving || selectedPlanId === (organisation?.planId || LEGACY_VALUE)}
              onClick={savePlan}>
              <Save size={16} strokeWidth={1.5} />
              Save plan
            </Button>
          </div>
          {organisation?.subscriptionStatus === 'trialing' && (
            <p className='mt-3 text-sm text-info'>
              The plan is saved as the upgrade preference, but active trials retain full trial entitlements until conversion
              or expiry.
            </p>
          )}
        </div>
      )}

      <PlanEntitlementsSummary organisation={organisation} />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  updateOrganisationEntitlements: (orgDetails) =>
    dispatch(ORGANISATION_ACTIONS.updateOrganisationEntitlements(orgDetails)),
  getOrganisation: () => dispatch(ORGANISATION_ACTIONS.getOrganisation()),
});

export default connect(null, mapDispatchToProps)(OrganisationPlanSettings);
